<script>
  import { onMount } from "svelte";
  import Icon from "@iconify/svelte";

  import liveIcon from "@iconify/icons-mdi/broadcast";
  import historyIcon from "@iconify/icons-mdi/history";
  import runtimeIcon from "@iconify/icons-mdi/monitor-dashboard";
  import settingsIcon from "@iconify/icons-mdi/cog-outline";
  import pauseIcon from "@iconify/icons-mdi/pause";
  import playIcon from "@iconify/icons-mdi/play";
  import refreshIcon from "@iconify/icons-mdi/refresh";
  import logoutIcon from "@iconify/icons-mdi/logout";
  import menuIcon from "@iconify/icons-mdi/menu";
  import closeIcon from "@iconify/icons-mdi/close";
  import { locales, t } from "./lib/i18n.js";
  import LanguageSwitcher from "./components/LanguageSwitcher.svelte";

  import LiveView from "./LiveView.svelte";
  import History from "./History.svelte";
  import Runtime from "./Runtime.svelte";
  import Settings from "./Settings.svelte";
  import Login from "./Login.svelte";

  const isViteDev = location.hostname === "localhost" && location.port !== "8080";
  const API_BASE = isViteDev ? "http://localhost:8080" : "";

  const JSON_HEADERS = { Accept: "application/json" };
  const STORAGE_KEY = "flytrap_api_key";
  const LOCALE_STORAGE = "flytrap_locale";

  const AUTH_MODE_BOOT = (() => {
    if (typeof document === "undefined") return false;
    const s = document.querySelector('script[type="module"][data-authenticate]');
    const raw = (s?.dataset?.authenticate || "").toString().trim().toLowerCase();
    return raw === "true" || raw === "1" || raw === "yes" || raw === "on";
  })();

  let authMode = AUTH_MODE_BOOT;
  let screen = "live";
  let mobileMenuOpen = false;
  let locale = "en";

  let connected = false;
  let paused = false;
  let dashRefresh = null;

  let setup = null;
  let setupErr = "";

  let apiKey = "";
  let authed = !authMode;
  let booted = false;
  let authErr = "";

  let apiErrorToast = "";
  let apiErrorToastFade = false;
  function setApiErrorToast(msg) {
    if (!msg) {
      apiErrorToast = "";
      return;
    }
    apiErrorToastFade = false;
    apiErrorToast = msg;
    setTimeout(() => {
      apiErrorToastFade = true;
      setTimeout(() => {
        apiErrorToast = "";
      }, 400);
    }, 20000);
  }

  function readKey() {
    try {
      return (localStorage.getItem(STORAGE_KEY) || "").trim();
    } catch {
      return "";
    }
  }

  function readLocale() {
    try {
      const value = (localStorage.getItem(LOCALE_STORAGE) || "en").toLowerCase();
      return locales.includes(value) ? value : "en";
    } catch {
      return "en";
    }
  }

  function setKey(next) {
    const v = (next || "").trim();
    try {
      if (!v) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, v);
      window.dispatchEvent(new Event("flytrap-auth-changed"));
    } catch {}
  }

  function syncAuthFromStorage() {
    apiKey = readKey();
    authed = !authMode || apiKey !== "";

    if (!authed) {
      connected = false;
      paused = true;
    }
  }

  function authHeaders() {
    return apiKey ? { Authorization: `Bearer ${apiKey}` } : {};
  }

  function onUnauthorized(msg = t(locale, "auth_required")) {
    authErr = msg;
    authMode = true;
    authed = false;
    connected = false;
    paused = true;
    setup = null;
    setupErr = "";
    screen = "live";
    setKey("");
    apiKey = "";
  }

  function handleLocaleChange(code) {
    locale = locales.includes(code) ? code : "en";
    try {
      localStorage.setItem(LOCALE_STORAGE, locale);
    } catch {}
  }

  function go(to) {
    screen = to;
    mobileMenuOpen = false;
  }

  function doRefresh() {
    if (typeof dashRefresh === "function") dashRefresh();
  }

  function togglePause() {
    paused = !paused;
  }

  async function prefetchSetup() {
    setupErr = "";
    try {
      const res = await fetch(`${API_BASE}/setup`, {
        method: "GET",
        cache: "no-store",
        headers: { ...JSON_HEADERS, ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized(apiKey ? t(locale, "login_invalid_key") : t(locale, "auth_required"));
        return;
      }

      if (!res.ok) throw new Error(`setup failed: ${res.status}`);

      setup = await res.json();
      connected = setup.connected;
      if (authMode) authed = true;
    } catch (e) {
      setup = null;
      setupErr = e?.message || String(e);
      connected = false;
      setApiErrorToast(setupErr);
    }
  }

  onMount(() => {
    locale = readLocale();
    syncAuthFromStorage();
    booted = true;

    const onStorage = (e) => {
      if (e?.key === STORAGE_KEY) syncAuthFromStorage();
      if (e?.key === LOCALE_STORAGE) locale = readLocale();
    };
    const onCustom = () => syncAuthFromStorage();

    window.addEventListener("storage", onStorage);
    window.addEventListener("flytrap-auth-changed", onCustom);

    prefetchSetup();

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("flytrap-auth-changed", onCustom);
    };
  });

  $: if (authMode && !authed) {
    screen = "live";
    setup = null;
    setupErr = "";
  }

  let didPrefetchAfterLogin = false;
  $: if (authed && !didPrefetchAfterLogin) {
    didPrefetchAfterLogin = true;
    prefetchSetup();
  }
  $: if (!authed) {
    didPrefetchAfterLogin = false;
  }

  const iconBtnBase =
    "inline-flex items-center justify-center w-10 h-10 rounded border " +
    "transition-all select-none " +
    "border-white/10 bg-white/[0.035] hover:bg-white/[0.06] " +
    "text-zinc-100/90 hover:text-white";

  const dividerCls = "w-px h-6 bg-white/10 mx-2";
</script>

<div class="min-h-screen bg-black text-zinc-100">
  <div class="pointer-events-none fixed inset-0 -z-10">
    <div class="absolute inset-0 bg-[radial-gradient(900px_220px_at_18%_0%,rgba(255,255,255,0.07),transparent_60%)]"></div>
    <div class="absolute inset-0 bg-[radial-gradient(700px_240px_at_90%_10%,rgba(59,130,246,0.05),transparent_65%)]"></div>
    <div class="absolute inset-0 bg-gradient-to-b from-[#07070a] via-[#050508] to-black"></div>
  </div>

  {#if !booted}
    <div class="min-h-screen grid place-items-center text-zinc-400 text-sm">
      {t(locale, "loading")}
    </div>
  {:else if authMode && !authed}
    <Login
      apiBase={API_BASE}
      jsonHeaders={JSON_HEADERS}
      storageKey={STORAGE_KEY}
      error={authErr}
      {locale}
      onLocaleChange={handleLocaleChange}
      onSave={(k) => {
        authErr = "";
        setKey(k);
      }}
    />
  {:else}
    <div class="w-full pt-4 px-4 sm:px-6">
      <header class="sticky top-3 z-50 ui-topbar">
        <div class="w-full px-4 py-3">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div class="flex items-center justify-between sm:justify-start gap-3">
              <button type="button" class="flex items-center gap-3 cursor-pointer text-left" onclick={() => go("live")}>
                <div class="ui-logo text-semibold" aria-hidden="true">
                  <Icon icon="game-icons:venus-flytrap" class="text-white font-semibold" width="26" height="26" />
                </div>
                <div class="hidden sm:flex flex-col leading-tight">
                  <div class="text-lg font-semibold tracking-wide text-white">{t(locale, "app_name")}</div>
                  <div class="text-xs text-zinc-400">
                    {t(locale, "app_subtitle")}
                  </div>
                </div>
              </button>
              <!-- Hamburger: visible on mobile only -->
              <button
                type="button"
                class="sm:hidden inline-flex items-center justify-center w-10 h-10 rounded border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] text-zinc-100"
                aria-label={mobileMenuOpen ? t(locale, "close_menu") : t(locale, "open_menu")}
                onclick={() => (mobileMenuOpen = !mobileMenuOpen)}
              >
                <Icon icon={mobileMenuOpen ? closeIcon : menuIcon} width="20" height="20" />
              </button>
            </div>

            <!-- Status, controls, nav: hidden on small screens (only logo + hamburger show) -->
            <div class="hidden sm:flex items-center justify-end gap-2">
              <div class="flex h-10 items-center gap-2">
                <div class="inline-flex h-10 items-center gap-2 rounded border border-white/10 bg-white/[0.03] px-3">
                  <span
                    class={`h-2.5 w-2.5 rounded-full ${
                      connected ? "bg-emerald-400" : "bg-rose-400"
                    }`}
                  ></span>
                  <span class="text-sm text-zinc-200/90">
                    {connected ? t(locale, "connected") : t(locale, "disconnected")}
                  </span>
                </div>

                <button
                  type="button"
                  class={`${iconBtnBase} ${paused ? "opacity-80" : ""}`}
                  title={paused ? t(locale, "resume_polling") : t(locale, "pause_polling")}
                  onclick={togglePause}
                >
                  <Icon icon={paused ? playIcon : pauseIcon} width="18" height="18" />
                </button>

                <button
                  type="button"
                  class={iconBtnBase}
                  title={t(locale, "refresh")}
                  onclick={doRefresh}
                >
                  <Icon icon={refreshIcon} width="18" height="18" />
                </button>

                <div class={dividerCls}></div>
              </div>

              <!-- Desktop nav -->
              <nav class="topnav topnav--nowrap h-10">

                <button class="topnav__tab" class:is-active={screen === "history"} onclick={() => go("history")}>
                  <Icon icon={historyIcon} width="16" height="16" />
                  <span>{t(locale, "nav_history")}</span>
                </button>

                <button class="topnav__tab" class:is-active={screen === "settings"} onclick={() => go("settings")}>
                  <Icon icon={settingsIcon} width="16" height="16" />
                  <span>{t(locale, "nav_settings")}</span>
                </button>

                <button class="topnav__tab" class:is-active={screen === "runtime"} onclick={() => go("runtime")}>
                  <Icon icon={runtimeIcon} width="16" height="16" />
                  <span>{t(locale, "nav_runtime")}</span>
                </button>

              </nav>
              <LanguageSwitcher bind:locale onchange={handleLocaleChange} />

              {#if authMode}
                <div class="flex items-center gap-2">
                  <div class="w-px h-6 bg-white/10 mx-2"></div>
                  <button
                    type="button"
                    class="inline-flex items-center justify-center w-10 h-10 rounded border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] transition-all"
                    title={t(locale, "logout")}
                    aria-label={t(locale, "logout")}
                    onclick={() => {
                      authErr = "";
                      setKey("");
                    }}
                  >
                    <Icon icon={logoutIcon} width="18" height="18" />
                  </button>
                </div>
              {/if}
            </div>
          </div>

        </div>
      </header>

      <!-- API error toast (fades after 20s) -->
      {#if apiErrorToast}
        <div
          class="mx-4 sm:mx-6 mt-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200 transition-opacity duration-400 {apiErrorToastFade ? 'opacity-0' : 'opacity-100'}"
          role="alert"
        >
          {apiErrorToast}
        </div>
      {/if}

      <!-- Mobile full overlay menu -->
      {#if mobileMenuOpen}
        <div
          class="sm:hidden fixed inset-0 z-[100] flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label={t(locale, "menu")}
        >
          <div
            class="absolute inset-0 bg-black/90 backdrop-blur-sm"
            role="button"
            tabindex="-1"
            aria-label={t(locale, "close_menu")}
            onclick={() => (mobileMenuOpen = false)}
            onkeydown={(e) => e.key === 'Escape' && (mobileMenuOpen = false)}
          ></div>
          <div class="relative flex flex-col items-center justify-start pt-20 flex-1 p-6">
            <button
              type="button"
              class="absolute top-4 right-4 inline-flex items-center justify-center w-10 h-10 rounded border border-white/10 bg-white/[0.06] text-zinc-100"
              aria-label={t(locale, "close_menu")}
              onclick={() => (mobileMenuOpen = false)}
            >
              <Icon icon={closeIcon} width="20" height="20" />
            </button>
            <nav class="flex flex-col gap-3 w-full max-w-sm">
              <button class="topnav__tab w-full justify-center py-5 text-base" class:is-active={screen === "live"} onclick={() => go("live")}>
                <Icon icon={liveIcon} width="20" height="20" />
                <span>{t(locale, "nav_live")}</span>
              </button>
              <button class="topnav__tab w-full justify-center py-5 text-base" class:is-active={screen === "history"} onclick={() => go("history")}>
                <Icon icon={historyIcon} width="20" height="20" />
                <span>{t(locale, "nav_history")}</span>
              </button>
              <button class="topnav__tab w-full justify-center py-5 text-base" class:is-active={screen === "runtime"} onclick={() => go("runtime")}>
                <Icon icon={runtimeIcon} width="20" height="20" />
                <span>{t(locale, "nav_runtime")}</span>
              </button>
              <button class="topnav__tab w-full justify-center py-5 text-base" class:is-active={screen === "settings"} onclick={() => go("settings")}>
                <Icon icon={settingsIcon} width="20" height="20" />
                <span>{t(locale, "nav_settings")}</span>
              </button>
              <div class="flex justify-center pt-2">
                <LanguageSwitcher bind:locale onchange={handleLocaleChange} />
              </div>
            </nav>
          </div>
        </div>
      {/if}
    </div>

    {#if screen === "live"}
      <LiveView
        apiBase={API_BASE}
        {apiKey}
        {locale}
        bind:connected
        bind:paused
        setRefresh={(fn) => (dashRefresh = fn)}
        {onUnauthorized}
        onApiError={setApiErrorToast}
      />
    {:else if screen === "history"}
      <History
        apiBase={API_BASE}
        {apiKey}
        {locale}
        setRefresh={(fn) => (dashRefresh = fn)}
        {onUnauthorized}
        onApiError={setApiErrorToast}
      />
    {:else if screen === "runtime"}
      <Runtime apiBase={API_BASE} {apiKey} {locale} onApiError={setApiErrorToast} />
    {:else if screen === "settings"}
      <Settings apiBase={API_BASE} {apiKey} {setup} setupErr={setupErr} {locale} onApiError={setApiErrorToast} />
    {/if}
  {/if}
</div>
